<template>
  <v-form v-model="validForm" ref="formRef" lazy-validation>
    <v-dialog v-model="showInfoDialog" max-width="500px">
      <v-card>
        <v-toolbar color="kbBlue"
                   dark
                   flat>
          <v-toolbar-title>
            {{ $t('general.help') }}
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <v-row justify="center">
            <v-col cols="12">
              {{ $t('general.helpSimpleOptions') }}
            </v-col>

            <v-col cols="12"
                   v-if="helpImage === 'dependentOptions'">
              {{ $t('general.helpDependentOptions') }}
            </v-col>

            <v-img max-width="300px"
                   :src="`https://storage.googleapis.com/keybe/app/images/${helpImage}.png`">
            </v-img>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer/>

          <v-btn @click="showInfoDialog = false"
                 color="kbBlue"
                 dark>
            {{ $t('general.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row dense
           style="min-height: 80vh"
           align="center"
           v-if="loadingForm">
      <v-row>
        <v-col class="text-center"
               cols="12">
          <v-progress-circular color="kbBlue"
                               indeterminate
                               :size="80">
          </v-progress-circular>
        </v-col>
      </v-row>
    </v-row>

    <v-row dense
           style="min-height: 80vh"
           align="center"
           v-if="isUpdateForm && errorLoadingForm">
      <v-row>
        <v-col class="text-center" cols="12">
          <v-icon color="error" size="60px">
            mdi-alert
          </v-icon>
        </v-col>

        <v-col class="text-center text-h6" cols="12">
          {{ $t('general.errorGettingTheForm') }}
        </v-col>

        <v-col class="text-center"
               cols="12">
          <v-btn @click="cancel"
                 color="red"
                 dark>
            {{ $t('general.cancel')}}
          </v-btn>
        </v-col>
      </v-row>
    </v-row>

    <v-row class="px-2" v-else>
      <v-col cols="12">
        <v-text-field :label="$t('general.name')"
                      color="kbDarkBlue"
                      :rules="[rules.required]"
                      v-model="form.name">
        </v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field :label="$t('general.code')"
                      color="kbDarkBlue"
                      v-model="form.code">
        </v-text-field>
      </v-col>

      <v-col cols="12">
        <v-text-field :label="$t('general.description')"
                      color="kbDarkBlue"
                      v-model="form.description">
        </v-text-field>
      </v-col>

      <v-col cols="12">
        <v-checkbox color="kbBlue"
                    :disabled="form.loginRequired"
                    :label="$t('general.registerForm')"
                    v-model="form.registerForm">
        </v-checkbox>
      </v-col>

      <v-col cols="12">
        <v-checkbox color="kbBlue"
                    :disabled="form.registerForm"
                    v-model="form.loginRequired"
                    :label="$t('general.obligateLogIn')">
        </v-checkbox>
      </v-col>

      <v-col cols="12">
        <v-select v-model="form.rolId"
                  v-if="form.loginRequired"
                  color="kbDarkBlue"
                  :label="$t('general.assignRoleWhenFormIsCompleted')"
                  :items="roles"
                  item-text="name"
                  item-value="id">
        </v-select>
      </v-col>

      <v-col cols="12">
        <h5 class="kbDarkBlue--text">
          {{$t('general.setUpNotificationsEmailForm')}}
        </h5>
      </v-col>

      <v-col class="mb-4" cols="12" >
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <h6 class="kbDarkBlue--text">{{$t('general.email')}}</h6>
              </v-col>

              <v-col cols="12">
                <v-checkbox color="kbBlue"
                            :label="$t('general.sendEmailOnSubmit')"
                            hide-details
                            v-model="form.sendEmail"></v-checkbox>
              </v-col>

              <v-col cols="12" v-if="form.sendEmail">
                <v-text-field color="kbDarkBlue"
                              v-model="form.email.subject"
                              :label="$t('general.emailSubject')"
                              :rules="[rules.required]">
                </v-text-field>
              </v-col>

              <template v-for="(email, index) in form.email.to">
                <v-col cols="12"
                       v-if="form.sendEmail"
                       :key="index">
                  <v-text-field v-model="form.email.to[index]"
                                color="kbDarkBlue"
                                :label="$t('general.email')"
                                :rules="[rules.email, rules.required]">
                    <template #append-outer>
                      <v-icon v-if="form.email.to.length > 1"
                              color="red"
                              @click="removeEmail(index)">
                        clear
                      </v-icon>
                      <v-icon color="kbDarkBlue"
                              v-if="index === form.email.to.length - 1"
                              @click="addEmail()">
                        add
                      </v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </template>

              <v-col cols="12">
                <v-divider/>
              </v-col>

              <v-col cols="12">
                <h6 class="kbDarkBlue--text">{{$t('general.phone')}}</h6>
              </v-col>

              <v-col cols="12">
                <v-checkbox color="kbBlue"
                            hide-details
                            :label="$t('general.sendSmsOnSubmit')"
                            v-model="form.sendSMS"></v-checkbox>
              </v-col>

              <v-col cols="12" v-if="form.sendSMS">
                <CountryPhonePicker v-model="form.phone.phone"
                                    :rules="[rules.required]"/>

                <v-text-field v-model="form.phone.message"
                              color="kbDarkBlue"
                              :label="$t('general.message')"
                              :rules="[rules.required]">
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <h3 class="kbDarkBlue--text">{{$t('general.fields')}}</h3>
      </v-col>

      <field @openHelpModal="openHelpDialog"
             v-for="(field, index) in form.fields"
             :key="index"
             :field="field"
             :fields="form.fields"
             :index="index"
             :has-register-email="hasRegisterEmail"
             :has-register-name="hasRegisterName"
             :has-register-phone="hasRegisterPhone"
             :form="form">
      </field>

      <v-col cols="12">
        <v-btn block
               color="kbBlue"
               dark
               @click="addField">
          <v-icon left>
            add
          </v-icon>
          {{$t('general.addField')}}
        </v-btn>
      </v-col>

      <v-col class="text-right"
             cols="12">
        <v-btn class="mr-2"
               color="red"
               large
               outlined
               @click="cancel">
          {{ $t('general.cancel') }}
        </v-btn>

        <v-btn color="kbBlue"
               dark
               @click="submit"
               large
               :loading="creatingEditingForm">
          {{ isUpdateForm ? $t('general.editForm') : $t('general.addNewForm') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import CountryPhonePicker from '@/components/ui/CountryPhonePicker.vue'
import Field from '@/pages/App/Forms/dialogs/Field.vue'
import notifications from '@/libs/notifications'
import rules from '@/libs/rules'
import { mapState } from 'vuex'
export default {
  components: {
    CountryPhonePicker, Field
  },
  computed: {
    ...mapState({
      roles: state => state.roles.roles,
      selectedForm: state => state.forms.selectedForm
    }),
    hasRegisterPhone () {
      return this.form.fields.findIndex(field => field.type === 'register-phone') >= 0
    },
    hasRegisterEmail () {
      return this.form.fields.findIndex(field => field.type === 'register-email') >= 0
    },
    hasRegisterName () {
      return this.form.fields.findIndex(field => field.type === 'register-name') >= 0
    },
    isUpdateForm () {
      return this.$route.name === 'Editar Formulario'
    }
  },
  created () {
    if (this.isUpdateForm) this.getFormData()
  },
  data: () => ({
    colorPickerDialog: false,
    creatingEditingForm: false,
    errorLoadingForm: false,
    field: {
      dependsOn: '',
      name: '',
      type: '',
      isRequired: false,
      isUnique: false,
      options: {
        includeOthersOption: false,
        items: [],
        itemsDependents: [],
        size: 'md12',
        row: false,
        terms: '',
        dataHandling: '',
        fileQuantity: 0
      }
    },
    form: {
      name: '',
      code: '',
      description: '',
      loginRequired: false,
      rolId: '',
      email: {
        subject: '',
        to: ['']
      },
      phone: {
        phone: {
          country: 'Colombia',
          code: 'co',
          number: '57',
          phone: ''
        },
        message: ''
      },
      fields: [],
      registerForm: false,
      sendEmail: false,
      sendSMS: false
    },
    helpImage: '',
    loadingForm: false,
    validForm: true,
    rules: rules,
    showInfoDialog: ''
  }),
  methods: {
    async submit () {
      if (!this.$refs.formRef.validate()) return
      this.creatingEditingForm = true

      try {
        this.isUpdateForm ? await this.updateForm() : await this.createForm()
        this.cancel()
      } catch (e) {
        this.creatingEditingForm = false
        notifications.error(e.message)
      }
    },
    async createForm () {
      await this.$store.dispatch('createForm', this.form)
      notifications.success(this.$t('general.formSuccessfullyCreated'))
      this.creatingEditingForm = false
    },
    openHelpDialog (value) {
      this.helpImage = value
      this.showInfoDialog = true
    },
    async updateForm () {
      if (this.form.registerForm && !this.hasRegisterEmail && !this.hasRegisterPhone) {
        this.creatingEditingForm = false
        return notifications.error(this.$t('general.needToSelectRegisterEmailOrPhone'))
      }

      await this.$store.dispatch('updateForm', this.form)

      this.$refs.formRef.reset()
      notifications.success(this.$t('general.formSuccessfullyEdited'))
    },
    addEmail () {
      this.form.email.to.push('')
    },
    addField () {
      this.form.fields.push(JSON.parse(JSON.stringify(this.field)))
    },
    async getFormData () {
      try {
        this.loadingForm = true
        await this.$store.dispatch('getForm', this.$route.params.formid)
        this.form = { ...this.form, ...this.selectedForm }
        this.loadingForm = false
      } catch (e) {
        console.log(e)
        this.errorLoadingForm = true
        this.loadingForm = false
      }
    },
    removeEmail (index) {
      this.form.email.to.splice(index, 1)
    },
    cancel () {
      this.$router.replace({ name: 'Formularios', params: { appid: this.$route.params.appid } })
    }
  },
  watch: {
    'form.registerForm' (value) {
      if (!value) {
        this.form.fields = this.form.fields.filter(item => {
          return item.type !== 'register-phone' && item.type !== 'register-email'
        })
      }
    }
  }
}
</script>
